import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppTokenModel, DynamicObjectInterface } from '../../../sdk/type';
import {
  Input,
  InputTypes,
  MLPVersion,
  Theme,
  FAIcon,
  IconStyle,
  ButtonIconPosition,
  ButtonStyles,
  Button,
  ButtonSize,
  DropDown,
  RadioButton,
  Modal,
  ButtonTypes,
  TextAlign,
  Tooltip,
  TooltipPreference,
  Checkbox,
  InputValidations,
  useBreakpoint,
} from '../../../ui/components';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import { useAPICall } from '../../../hooks';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import ShareDrawer from '../../../ui/components/DrawerAlt/ShareDrawer';
import { isValidEmail } from '../../../ui/utils/common';
import { PAGES, trackPage } from '../../../ui/utils';
import TeamService from '../../../sdk/services/teamService';
import CampaignService from '../../../sdk/services/campaignService';
import userService from '../../../sdk/services/userService';
import CoachService from '../../../sdk/services/coachService';
import ParticipantService from '../../../sdk/services/participantService';
import CreateTeamImage from '../../../assets/image/operationCard/Create_Team.png';
import JoinTeamImage from '../../../assets/image/operationCard/Join_Team.png';
import styles from './index.module.scss';

interface CreateTeamData {
  teamId: number | null;
  teamName: string;
  description?: string;
  campaignId: string;
  teamLeader?: string;
  coachId?: string;
  teamStartDate: string;
  teamEndDate: string;
  status: string;
  remark?: string;
  isIndividualTeam?: number | null;
  participant?: string[];
  checkbox?: string;
  individualCheck?: string;
  coachDetail?: {};
  campaignName?: string;
  campaignRecId?: number;
  pin?: number;
  highestPin?: number;
}

const initTeamForm: CreateTeamData = {
  teamId: null,
  teamName: '',
  campaignId: '',
  coachId: '',
  teamLeader: '',
  teamStartDate: '',
  teamEndDate: '',
  status: '',
  remark: '',
  isIndividualTeam: null,
  participant: [],
  checkbox: '',
  individualCheck: '',
  coachDetail: {},
  campaignName: '',
};

interface CreateTeamProps {
  decodedData: AppTokenModel | null;
}

export const CreateTeam = (props: CreateTeamProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { adminToast } = useAdminToast();
  const id = searchParams.get('');

  const [masterList, setMasterList] = useState<{
    coachList: DynamicObjectInterface;
    campaignList: DynamicObjectInterface;
  }>();
  //const [filteredCoachList, setFilteredCoachList] = useState(masterList?.coachList);
  const [teamData, setTeamData] = useState<CreateTeamData>(initTeamForm);
  const [teamSize, setTeamSize] = useState(0);
  const [individualCheck, setIndividualCheck] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showShareDrawer, setShowShareDrawer] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showNoCampaignPopup, setShowNoCampaignPopup] = useState(false);
  const [coachName, setCoachName] = useState<string>('');
  const [coachInvalid, setCoachInvalid] = useState(false);
  const [joinTeamLink, setJoinTeamLink] = useState<string>('');
  const [clickedFields, setClickedFields] = useState({
    campaignId: false,
    teamName: false,
    teamLeader: false,
    participant: [],
  });
  const [invalid, setInvalid] = useState(true);
  const [emailInvalid, setEmailInvalid] = useState<{
    teamLeader: boolean;
    [key: string]: boolean | undefined;
  }>({ teamLeader: false });
  const [validateUser, setValidateUser] = useState(true);

  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;
  const apiCall = useAPICall();

  // const updateCoachEnrollmentList = async (campaignId: string) => {
  //   const res = await apiCall(EnrollmentService.getEnrollmentList, 'COACH', campaignId);
  //   const coachEnrollmentList = res?.data?.data?.map((d: DynamicObjectInterface) => ({
  //     ...d,
  //     coachDetail: d,
  //     id: d.coachId,
  //     title: `${d.coachId} - ${d.name}`,
  //   }));
  //   setMasterList((i: DynamicObjectInterface) => {
  //     i.coachList = coachEnrollmentList;
  //     return { ...i };
  //   });
  //   setFilteredCoachList(coachEnrollmentList);
  // };

  useEffect(() => {
    if (teamData?.campaignRecId) {
      validateCoach();
    }
  }, [teamData?.campaignRecId]);

  const validateCoach = async () => {
    const response = await apiCall(ParticipantService.validatePin, {
      campaignRecId: teamData?.campaignRecId,
    });
    setValidateUser(response?.data?.data?.canEnrollParticipant);
    setTeamData(prevTeamData => ({
      ...prevTeamData,
      pin: response?.data?.data?.pin,
      highestPin: response?.data?.data?.highestPin,
    }));
  };

  const handleInputChange = async (name: string, value: DynamicObjectInterface) => {
    if (name.startsWith('participant_')) {
      setTeamData((prevTeamData: DynamicObjectInterface) => ({
        ...prevTeamData!,
        participant: {
          ...prevTeamData.participant,
          [name]: value,
        },
      }));
      setClickedFields(prevClickedFields => ({
        ...prevClickedFields,
        participant: {
          ...prevClickedFields.participant,
          [name]: value === '',
        },
      }));
      setEmailInvalid((prevData: DynamicObjectInterface) => ({
        ...prevData!,
        [name]: value !== '' && !isValidEmail(value as string),
      }));
    } else if (name === 'coachId') {
      setTeamData(prevTeamData => ({
        ...prevTeamData!,
        [name]: value,
      }));
      setClickedFields(prevClickedFields => ({
        ...prevClickedFields,
        [name]: value === '',
      }));

      if (value.length === 10) {
        try {
          const response = await apiCall(CoachService.getNameByCoachId, teamData.campaignId, value);
          if (response?.data?.data?.coachName) {
            setCoachName(response.data.data.coachName);
            setCoachInvalid(false);
          } else {
            setCoachInvalid(true);
            setCoachName('');
          }
        } catch (error) {
          setCoachName('');
          setCoachInvalid(true);
        }
      } else {
        if (value.length === 0) {
          setCoachInvalid(false);
        } else {
          setCoachInvalid(true);
        }
        setCoachName('');
      }
    } else {
      if (name === 'checkbox' && teamData?.checkbox) {
        value = '';
      }

      if (name === 'individualCheck') {
        setClickedFields({
          campaignId: false,
          teamName: false,
          teamLeader: false,
          participant: [],
        });
        setEmailInvalid({ teamLeader: false });

        if (teamData.individualCheck) {
          value = '';
          setIndividualCheck(false);
          teamData.teamLeader = '';
          teamData.teamName = '';
          teamData.isIndividualTeam = 0;
          teamData.participant = [];
        } else {
          setIndividualCheck(true);
          let res = await apiCall(userService.getUserDetails, `${props.decodedData?.userId}`);
          teamData.teamLeader = res?.data?.data?.email ? res?.data?.data?.email : '';
          teamData.teamName = `Individual`;
          teamData.isIndividualTeam = 1;
          teamData.participant = [];
        }
      }

      if (name === 'isIndividualTeam') {
        teamData.isIndividualTeam = value;
        if (value === 1) {
          teamData.teamName = 'Individual';
          setClickedFields({
            campaignId: false,
            teamName: false,
            teamLeader: false,
            participant: [],
          });
          setEmailInvalid({ teamLeader: false });
        } else {
          teamData.teamName = '';
        }
      }

      if (name === 'campaignId') {
        //await updateCoachEnrollmentList(value);
        setEmailInvalid({ teamLeader: false });
        setTeamData((prevTeamData: DynamicObjectInterface) => ({
          ...prevTeamData!,
          teamLeader: individualCheck ? prevTeamData.teamLeader : '',
          participant: [],
        }));

        let campaignRecId = masterList?.campaignList?.find((c: DynamicObjectInterface) => c.value === value)?.recId;

        let result = await apiCall(CampaignService.getCampaignDetail, `${campaignRecId}`);
        setTeamSize(result?.data?.data?.activities?.participant?.teamSize);
      }

      if (name === 'teamLeader') {
        setEmailInvalid((prevData: DynamicObjectInterface) => ({
          ...prevData!,
          [name]: !isValidEmail(value as string),
        }));
      }

      setTeamData(prevTeamData => ({
        ...prevTeamData!,
        [name]: value,
      }));

      setClickedFields(prevClickedFields => ({
        ...prevClickedFields,
        [name]: value === '',
      }));
    }
  };

  // const handleCoachIdChange = async (e: DynamicObjectInterface) => {
  //   const coachId = e.target.value;
  //   //handleInputChange('coachId', coachId);
  //   try {
  //     const res = await apiCall(CoachService.getNameByCoachId, teamData?.campaignId, coachId);
  //     if (res) {
  //       console.log(res.data);
  //     } else {
  //       console.log("failed")
  //     }
  //   } catch (error) {
  //     console.error('Error validating coachId:', error);
  //   }
  // };

  const onSave = async () => {
    const payload = {
      campaignId: teamData?.campaignId,
      campaignName: teamData?.campaignName,
      coachDetail: teamData.coachDetail,
      teamName: teamData?.teamName,
      coachId: teamData?.coachId,
      isIndividualTeam: teamData.isIndividualTeam || 0,
      memberEmails: !individualCheck
        ? [
            ...Object.values(teamData?.participant ?? {}).map(email => ({ email, isTeamLead: false })),
            { email: teamData?.teamLeader, isTeamLead: true },
          ]
        : [],
      loginId: '',
      description: teamData?.remark,
      startDate: new Date().toISOString().slice(0, -1),
    };

    let result = await apiCall(TeamService.createTeam, payload);

    if (!result?.data?.result) {
      if (result.status !== 500) {
        adminToast({
          message: result?.data?.message,
          theme: Theme.ERROR,
        });
      }
    } else {
      setJoinTeamLink(result?.data?.link);
      const encodedValue = new URLSearchParams(result?.data?.link.split('?')[1]).get('t');
      if (encodedValue && individualCheck) {
        const decoded = atob(encodedValue);
        const values = decoded.split(',').map(Number);
        window.location.href = ` join-team/register?cmI=${values[0]}&tmI=${values[1]}&tmL=${values[2]}`;
      } else {
        setShowConfirmation(false);
        setShowNotification(true);
      }
    }
  };

  // const searchCoach = (input: string) => {
  //   if (!input) {
  //     setFilteredCoachList(masterList?.coachList);
  //   } else {
  //     const filteredUsers = masterList?.coachList?.filter((coach: DynamicObjectInterface) => {
  //       return coach.title.toLowerCase().includes(input.toLowerCase());
  //     });
  //     setFilteredCoachList(filteredUsers);
  //   }
  // };

  const teamDetailsBtnConfig = [
    {
      title: t('admin.teamCreate.continue'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: invalid,
      onClick: () => {
        individualCheck ? onSave() : setShowConfirmation(true);
      },
    },
  ];

  // const handleJoinTeam = () => {
  //   const encodedValue = new URLSearchParams(joinTeamLink.split('?')[1]).get('t');

  //   if (encodedValue) {
  //     const decoded = atob(encodedValue);
  //     const values = decoded.split(',').map(Number);
  //     window.location.href = ` join-team/register?cmI=${values[0]}&tmI=${values[1]}&tmL=${values[2]}`;
  //   }

  //   setShowNotification(false);
  // };

  const confirmationPrompt = (
    <>
      {showConfirmation && (
        <Modal
          primaryButtonLabel={''}
          headline={'Confirmation'}
          children={
            <>
              <div
                style={{
                  fontSize: '16px',
                  fontFamily: 'GT-Walsheim-Pro-Bold, sans-serif',
                  lineHeight: '24px',
                  margin: '10px 0',
                }}
              >
                {t('admin.teamCreate.confirmation')}
              </div>
              <div style={{ display: 'flex', margin: '32px 0' }}>
                <Checkbox
                  id={'checkbox_dashboard_confirmation'}
                  className={styles['checkbox']}
                  checked={teamData.checkbox === 'on'}
                  theme={Theme.BLACK}
                  text={
                    <p
                      id="agree-checkbox"
                      dangerouslySetInnerHTML={{
                        __html: t('admin.teamCreate.termsLine1').replace(
                          '{terms-conditions}',
                          `<a href="/terms-and-conditions?cid=${teamData?.campaignId}" target="_blank">${t('securityPrivacy.terms-condition')}</a>`,
                        ),
                      }}
                    />
                  }
                  onChange={e => handleInputChange('checkbox', e.target.value)}
                  onClick={e => e.stopPropagation()}
                />
                <>
                  {/* {t('admin.teamCreate.termsLine1')} */}
                  <br />
                  {/* {t('admin.teamCreate.termsLine2')} */}
                </>
              </div>
            </>
          }
          closeIcon={false}
          containerWidth={'max-content'}
          textAlign={TextAlign.LEFT}
          primaryButton={
            <Button
              title={t('admin.teams.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                onSave();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
              disabled={!teamData?.checkbox}
            />
          }
          secondaryButton={
            <Button
              title={t('admin.teams.back')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowConfirmation(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );

  const noCampaignPrompt = (
    <>
      {showNoCampaignPopup && (
        <Modal
          primaryButtonLabel={''}
          headline={''}
          closeIcon={false}
          containerWidth={isDesktopView ? '60%' : '80%'}
          primaryButton={
            <Button
              title={t('Back to Homepage')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                navigate(`/`);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={undefined}
        >
          <div>
            <div>
              <img src={JoinTeamImage} alt={`JoinTeamImage`} height={isDesktopView ? '400px' : '200px'} />
            </div>
            <div className={styles['sub-header']}>
              Team setup and registration is now closed. Stay tuned for further updates.
            </div>
          </div>
        </Modal>
      )}
    </>
  );

  const notificationPrompt = (
    <>
      {!individualCheck && showNotification && (
        <Modal
          primaryButtonLabel={''}
          containerWidth={'max-content'}
          headline={''}
          closeIcon={true}
          onClose={() => {
            navigate(`/`);
          }}
          textAlign={TextAlign.CENTER}
          primaryButton={
            <Button
              title={t('admin.teamCreate.backToDashboard')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                //handleJoinTeam();
                // setShowNotification(false);
                navigate('/');
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.teams.share')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowShareDrawer(true);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              icon={{ iconName: 'share-alt', iconStyle: IconStyle.SOLID }}
              iconPosition={ButtonIconPosition.LEFT}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        >
          <div className={styles['modal-container']}>
            {/* <p>{joinTeamLink}</p> */}
            <div className={styles['modal-img']}>
              <img src={CreateTeamImage} alt={`createTeamImage`} width="295px" />
            </div>
            <div className={styles['modal-headline']}>{t('admin.dashboard.teamSetup')}</div>
            <div className={styles['modal-desc']}>{t('admin.dashboard.sentEmail')}</div>
          </div>
        </Modal>
      )}
    </>
  );

  const participantInput = Array.from({ length: teamSize - 1 }, (_, index) => {
    const plusOne = index + 1;
    return (
      <div>
        <p className={styles['input-label']}>
          {t('admin.teamCreate.participant')} {plusOne + 1}
        </p>
        <Input
          key={`${teamData?.campaignId}_participant_${index}`}
          type={InputTypes.TEXT}
          name={`participant_${index + 1}`}
          mlpVersion={MLPVersion.ONE}
          onChange={e => {
            handleInputChange(`participant_${index}`, e.target.value);
          }}
          status={emailInvalid?.[`participant_${index}`] ? InputValidations.INVALID : InputValidations.DEFAULT}
          message={emailInvalid?.[`participant_${index}`] ? `${t('admin.teamCreate.invalidEmailFormat')}` : ''}
        />
      </div>
    );
  });

  const sharePrompt = (
    <>
      {showShareDrawer && (
        <ShareDrawer
          open={!!joinTeamLink}
          onClose={() => setShowShareDrawer(false)}
          title="Share Via"
          programMessage={`You have been invited to join ${teamData?.campaignName} at ${joinTeamLink}`}
          programUrl={joinTeamLink}
          channels={['FaWhatsapp', 'FaLink']}
          programTitle=""
        />
      )}
    </>
  );

  const validateInputs = () => {
    const { teamName, campaignId, participant } = teamData;
    const requiredFields = [teamName, campaignId, participant];
    const hasEmptyFields = requiredFields.some(field => field === '');

    if (hasEmptyFields || emailInvalid.teamLeader || !teamData.teamLeader || coachInvalid || !validateUser) {
      setInvalid(true);
    } else {
      setInvalid(false);
    }
  };

useEffect(() => {
  (async () => {
    let apiList = [apiCall(CampaignService.getCampaigns)];
    let res = await Promise.all(apiList);

    let masterList = {
      coachList: [],
      campaignList: (
        res?.[0]?.data?.data.filter(
          (i: DynamicObjectInterface) => {
            const registerEndTime = new Date(i.registerEnd).getTime();
            const nowTime = new Date().getTime();
            return (
              registerEndTime > nowTime &&
              i.status !== 'DELETED' &&
              i.status !== 'CLOSED'
            );
          }
        ) || []
      ).map((d: DynamicObjectInterface) => ({
        ...d,
        campaignName: d.campaignName,
        campaignRecId: d.recId,
        value: d.campaignId,
        title: `${d.campaignId} - ${d.campaignName}`,
      })),
    };

    if (masterList?.campaignList?.length > 0) {
      setMasterList(masterList);
      setTeamData(prevTeamData => ({
        ...prevTeamData,
        campaignName: masterList?.campaignList[0].campaignName,
        campaignId: masterList?.campaignList[0].value,
        campaignRecId: masterList?.campaignList[0].recId,
      }));
      let campaignRecId = masterList?.campaignList?.[0]?.recId;
      let result = await apiCall(CampaignService.getCampaignDetail, `${campaignRecId}`);
      setTeamSize(result?.data?.data?.activities?.participant?.teamSize);
    } else {
      setShowNoCampaignPopup(true);
    }
  })();

  const timeout = setTimeout(() => {
    trackPage(PAGES.createTeam);
  }, 1000);

  return () => {
    clearTimeout(timeout);
  };
}, []);


  useEffect(() => {
    validateInputs();
  }, [teamData, coachInvalid]);

  return (
    <div className={styles['edit-team']}>
      <div className={styles['card']}>
        <div className={styles['card-header']}>
          <h2>{t('admin.teamCreate.title')}</h2>
          {isDesktopView && (
            <>
              <div className={styles['actions-container']}>
                {teamDetailsBtnConfig.map(btn => (
                  <Button key={btn.title} size={ButtonSize.SMALL} {...btn} />
                ))}
              </div>
            </>
          )}
        </div>
        <div className={styles['create-team-form-container']}>
          <div className={styles['dropdown-wrapper']}>
            <DropDown
              name="campaignId"
              dropdownLabel={`${t('admin.teams.campaign')}`}
              required={true}
              disabledFilter={false}
              mlpVersion={MLPVersion.ONE}
              value={
                masterList?.campaignList?.find((c: DynamicObjectInterface) => c.value === teamData?.campaignId)?.title
              }
              data={masterList?.campaignList}
              configData={{
                label: 'title',
                onSelect: (selectedOption: DropDownData) => {
                  handleInputChange('campaignId', selectedOption.value);
                  handleInputChange('campaignName', selectedOption.campaignName);
                  handleInputChange('campaignRecId', selectedOption.campaignRecId);
                },
              }}
              readOnly={false}
            />
            { teamData?.pin === undefined ? (
              <div className={styles['validate-user-container']}>
                <FAIcon theme={Theme.WARNING} iconName="exclamation-circle" iconStyle={IconStyle.SOLID} />
                <div
                  className={styles['error-text']}
                >{`Oops! An unexpected error has occurred. Please try again later, or contact Customer Service for support.`}</div>
              </div>
            ) : (
              !validateUser && (
                <div className={styles['validate-user-container']}>
                  <FAIcon theme={Theme.WARNING} iconName="exclamation-circle" iconStyle={IconStyle.SOLID} />
                  <div
                    className={styles['error-text']}
                  >{`Oops! Our records indicate you do not fulfil the conditions for creating a new team. If you believe this is in error, please contact Customer Service for support.`}</div>
                </div>
              )
            )}
          </div>
          <div className={styles['search-coach-container']}>
            <div className={styles['input-wrapper']}>
              <Input
                key={id}
                type={InputTypes.TEXT}
                name={'coachId'}
                label={`${t('admin.teams.coachId')}`}
                value={coachName ? coachName : teamData.coachId}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChange('coachId', e.target.value);
                }}
                status={
                  coachInvalid
                    ? InputValidations.INVALID
                    : coachName.length > 0
                      ? InputValidations.VALID
                      : InputValidations.DEFAULT
                }
                message={''}
              />
              {teamData && teamData.coachId && teamData.coachId.length > 0 && (
                <FAIcon
                  className={styles['close-icon']}
                  iconStyle={IconStyle.SOLID}
                  data-testid="close-icon-test-id"
                  iconName={'close'}
                  theme={Theme.BLACK}
                  onClick={() => {
                    handleInputChange('coachId', '');
                  }}
                />
              )}
            </div>
            {coachInvalid && <div className={styles['error-message']}>{t('admin.teamCreate.invalidCoachId')}</div>}
          </div>
        </div>
        {!individualCheck && (
          <div className={styles['create-team-form-container']}>
            <Input
              key={id}
              type={InputTypes.TEXT}
              name={'teamName'}
              label={`${t('admin.teams.teamName')}`}
              value={teamData?.teamName}
              required
              mlpVersion={MLPVersion.ONE}
              onChange={e => {
                handleInputChange('teamName', e.target.value);
              }}
              disabled={teamData?.isIndividualTeam === 1}
              status={clickedFields.teamName ? InputValidations.INVALID : InputValidations.DEFAULT}
              message={clickedFields.teamName ? `${t('admin.teamCreate.teamNameRequired')}` : ''}
            />
            <div className={styles['input-status']}>
              <div className={styles['input-label']}>
                <span className={styles['label-with-tooltip']}>
                  {t('admin.teams.isIndividualTeam')}
                  <Tooltip
                    message='Select "Team" to invite multiple persons to participate as a team, or "Individual" to invite one person to participate individually.'
                    preference={TooltipPreference.RIGHT}
                    className={styles['input-tool-tip']}
                    messageClassName={styles['input-tool-tip-message']}
                  />
                </span>
              </div>
              <div className={styles['input-radio']}>
                <RadioButton
                  label="Team"
                  checked={teamData?.isIndividualTeam !== 1}
                  value={0}
                  onChange={e => handleInputChange('isIndividualTeam', Number(e.target.value))}
                />
                <RadioButton
                  label="Individual"
                  checked={teamData?.isIndividualTeam === 1}
                  value={1}
                  onChange={e => handleInputChange('isIndividualTeam', Number(e.target.value))}
                />
              </div>
            </div>
            {/* <SearchComponent
            label={t('admin.teams.coachId')}
            placeholder={t('admin.teams.searchCoach')}
            initialSearchText={
              masterList?.coachList?.find((c: DynamicObjectInterface) => c.id === teamData?.coachId)?.title
            }
            status={InputValidations.DEFAULT}
            labelChoice={'title'}
            dropdownOptions={filteredCoachList}
            onSearch={searchCoach}
            onClear={() => {
              searchCoach('');
              handleInputChange('coachId', '');
              handleInputChange('coachDetail', '');
            }}
            onSelect={e => {
              handleInputChange('coachId', e.id);
              handleInputChange('coachDetail', e.coachDetail);
            }}
          /> */}
          </div>
        )}
        <div className={styles['create-team-form-container']}>
          <Input
            key={id}
            type={InputTypes.TEXT}
            name={'remark'}
            label={`${t('admin.teams.remark')}`}
            value={teamData?.remark}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('remark', e.target.value);
            }}
          />
        </div>
        <div className={styles['create-team-form-container']}>
          <div className={styles['dropdown-wrapper']}>
            <Checkbox
              id={'checkbox_dashboard_individual'}
              className={styles['checkbox']}
              checked={teamData?.individualCheck === 'on'}
              theme={Theme.BLACK}
              text={`${t('admin.teams.individualCheck')}`}
              onChange={e => handleInputChange('individualCheck', e.target.value)}
            />
            {individualCheck && !teamData.teamLeader && (
              <div className={styles['validate-user-container']}>
                  <FAIcon theme={Theme.WARNING} iconName="exclamation-circle" iconStyle={IconStyle.SOLID} />
                  <div
                    className={styles['error-text']}
                  >{`Please update your email address on the Profile page before creating or enrolling in a campaign.`}</div>
              </div>
            )}
          </div>
        </div>
        {!individualCheck && (
          <div className={styles['card-send-email-invitation']}>
            <div className={styles['card-send-email-invitation-title']}>{`${t('admin.dashboard.formYourTeam')}`}</div>
            <div className={styles['card-send-email-invitation-desc']}>{`${t(
              'admin.dashboard.invitationDescription',
            )}`}</div>
            <div className={styles['card-send-email-invitation-input-container']}>
              <div className={styles['card-send-email-invitation-input-container-item']}>
                <p className={styles['input-label-leader']}>{t('admin.teamCreate.teamLeader')}</p>
                {/* <div className={styles['input-wrapper']}> */}
                <Input
                  key={id}
                  type={InputTypes.TEXT}
                  name={'teamLeader'}
                  value={teamData?.teamLeader}
                  placeholder="Enter Valid Email Address"
                  mlpVersion={MLPVersion.ONE}
                  onChange={e => {
                    handleInputChange('teamLeader', e.target.value);
                  }}
                  status={
                    clickedFields.teamLeader || emailInvalid?.['teamLeader']
                      ? InputValidations.INVALID
                      : InputValidations.DEFAULT
                  }
                  message={
                    clickedFields.teamLeader
                      ? `${t('admin.teamCreate.emailRequired')}`
                      : emailInvalid?.['teamLeader']
                        ? `${t('admin.teamCreate.invalidEmailFormat')}`
                        : ''
                  }
                />
                {/* <div className={styles['input-message']}>*Only ABOs can be team leaders.</div> */}
                {/* </div> */}
              </div>
              {teamData.isIndividualTeam !== 1 && participantInput}
            </div>
          </div>
        )}
        <p
          className={styles['input-message-footnote']}
          dangerouslySetInnerHTML={{
            __html: t('admin.dashboard.joinTeamFootnote').replace('{one-month}', `<b>one month</b>`),
          }}
        />
        {/* <div className={styles['input-message']}>{`*${t('admin.dashboard.joinTeamFootnote')}`}</div> */}
        {!isDesktopView && (
          <>
            <div className={styles['actions-container']}>
              {teamDetailsBtnConfig.map(btn => (
                <Button key={btn.title} size={ButtonSize.SMALL} {...btn} />
              ))}
            </div>{' '}
          </>
        )}
      </div>
      {confirmationPrompt}
      {notificationPrompt}
      {noCampaignPrompt}
      {sharePrompt}
    </div>
  );
};
